import { useState, useEffect, useRef } from "react";
import { X, FileText, Edit, Save, Clock, Calendar } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useAuth } from "../../../../context/AuthContext";
import { AsambleaRecord } from "../../../../api/asambleaService";
import {
  obtenerPreguntasAsamblea,
  modificarPreguntaAsamblea,
  modificarEstatusPreguntaAsamblea,
  PreguntaAsambleaRecord,
} from "../../../../api/preguntaAsambleaService";
import { obtenerEstatusPreguntaAsamblea, EstatusPreguntaAsamblea } from "../../../../api/estatusPreguntaAsambleaService";
import Swal from "sweetalert2";

interface DetailPreguntaAsambleaModalProps {
  isOpen: boolean;
  pregunta: PreguntaAsambleaRecord | null;
  asamblea: AsambleaRecord | null;
  onClose: (shouldReload?: boolean) => void;
}

const formatDate = (value: string) => {
  if (!value || value.trim() === "") return "Sin fecha";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

export default function DetailPreguntaAsambleaModal({
  isOpen,
  pregunta,
  asamblea,
  onClose,
}: DetailPreguntaAsambleaModalProps) {
  const { session } = useAuth();
  const lastLoadedPreguntaRef = useRef<string>("");
  const isLoadingRef = useRef(false);

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [preguntaData, setPreguntaData] = useState<PreguntaAsambleaRecord | null>(pregunta);

  const [texto, setTexto] = useState("");
  const [conTiempo, setConTiempo] = useState<number>(0);
  const [minutos, setMinutos] = useState<number>(0);
  const [selectedEstatus, setSelectedEstatus] = useState("");

  const [estatusList, setEstatusList] = useState<EstatusPreguntaAsamblea[]>([]);
  const [isLoadingEstatus, setIsLoadingEstatus] = useState(false);

  const loadPreguntaDetails = async () => {
    if (!pregunta?.pra_codigo || !asamblea?.asa_codigo) return;

    try {
      setIsLoadingDetails(true);
      const response = await obtenerPreguntasAsamblea({
        asamblea_codigo: String(asamblea.asa_codigo),
        codigo: String(pregunta.pra_codigo),
      });

      if (response.code === "1" && Array.isArray(response.object_) && response.object_.length > 0) {
        const data = response.object_[0];
        setPreguntaData(data);
        setTexto(data.pra_texto);
        setConTiempo(data.pra_con_tiempo);
        setMinutos(data.pra_minutos || 0);
        setSelectedEstatus(data.pra_estatus || "");
      }
    } catch (error) {
      console.error("Error al cargar detalles de pregunta:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (isOpen && pregunta) {
      const preguntaId = String(pregunta.pra_codigo || "");
      const isNewPregunta = lastLoadedPreguntaRef.current !== preguntaId;

      if (isNewPregunta && !isLoadingRef.current) {
        lastLoadedPreguntaRef.current = preguntaId;
        loadPreguntaDetails().then(() => {
          loadEstatus();
        });
      } else if (!isNewPregunta) {
        setPreguntaData(pregunta);
        setTexto(pregunta.pra_texto);
        setConTiempo(pregunta.pra_con_tiempo);
        setMinutos(pregunta.pra_minutos || 0);
        setSelectedEstatus(pregunta.pra_estatus || "");
      }
    } else {
      setEstatusList([]);
      lastLoadedPreguntaRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pregunta?.pra_codigo]);

  const loadEstatus = async () => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      setIsLoadingEstatus(true);

      const estatusResponse = await obtenerEstatusPreguntaAsamblea();
      if (estatusResponse.code === "1" && estatusResponse.object_) {
        setEstatusList(estatusResponse.object_);
      }
      setIsLoadingEstatus(false);
    } catch (error) {
      console.error("Error al cargar estatus:", error);
    } finally {
      isLoadingRef.current = false;
    }
  };

  const handleSaveChanges = async () => {
    if (!texto.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese el texto de la pregunta",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (conTiempo === 1 && (!minutos || minutos <= 0)) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese los minutos cuando la pregunta usa tiempo",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const usuarioAdministrador = session?.email || "";
      const codigo = String(preguntaData?.pra_codigo || "");
      const asambleaCodigo = String(asamblea?.asa_codigo || "");

      if (!usuarioAdministrador || !codigo || !asambleaCodigo) {
        throw new Error("No se pudo obtener la información necesaria");
      }

      const payload = {
        codigo,
        asamblea_codigo: asambleaCodigo,
        usuario_administrador: usuarioAdministrador,
        texto: texto.trim(),
        con_tiempo: String(conTiempo),
        minutos: String(minutos),
      };

      const response = await modificarPreguntaAsamblea(payload);

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Cambios guardados!",
          text: response.message || "La pregunta se ha actualizado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
        setIsEditing(false);
        await loadPreguntaDetails();
        onClose(true);
      } else {
        throw new Error(response.message || "Error al actualizar la pregunta");
      }
    } catch (error) {
      console.error("Error al guardar cambios:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al actualizar la pregunta";
      const mensajeTraducido = errorMessage.includes("Request failed")
        ? "Error al actualizar la pregunta. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEstatusChange = async (estatusCodigo: string) => {
    const previousEstatus = selectedEstatus;
    setSelectedEstatus(estatusCodigo);

    try {
      const usuarioAdministrador = session?.email || "";
      const codigo = String(preguntaData?.pra_codigo || "");
      const asambleaCodigo = String(asamblea?.asa_codigo || "");

      if (!usuarioAdministrador || !codigo || !asambleaCodigo) {
        throw new Error("No se pudo obtener la información necesaria");
      }

      const payload = {
        codigo,
        asamblea_codigo: asambleaCodigo,
        usuario_administrador: usuarioAdministrador,
        estatus_codigo: estatusCodigo,
      };

      const response = await modificarEstatusPreguntaAsamblea(payload);

      if (response.code !== "1" && response.code !== 1) {
        setSelectedEstatus(previousEstatus);
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo cambiar el estatus",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
      } else {
        await loadPreguntaDetails();
      }
    } catch (error) {
      setSelectedEstatus(previousEstatus);
      console.error("Error al cambiar estatus:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cambiar el estatus",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    }
  };

  if (!isOpen || !pregunta || !asamblea) return null;

  const preguntaMostrar = preguntaData || pregunta;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-military-600 flex items-center gap-2">
              <FileText size={28} />
              Detalle de Pregunta
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Código: #{preguntaMostrar.pra_codigo} - Asamblea: {asamblea.asa_titulo}
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={isSubmitting || isLoadingDetails}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {isLoadingDetails ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
              <p className="text-gray-600">Cargando detalles...</p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estatus
                </label>
                {isLoadingEstatus ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                    <span className="text-sm">Cargando estatus...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {estatusList.map((estatus) => (
                      <label
                        key={estatus.eps_codigo}
                        className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="estatus"
                          value={estatus.eps_codigo}
                          checked={selectedEstatus === estatus.eps_codigo}
                          onChange={(e) => handleEstatusChange(e.target.value)}
                          disabled={isSubmitting}
                          className="text-military-500 focus:ring-military-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {estatus.eps_nombre}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText size={16} className="inline mr-1" />
                  Texto de la pregunta <span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <Textarea
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    disabled={isSubmitting}
                    className="min-h-[100px]"
                    required
                  />
                ) : (
                  <Textarea
                    value={preguntaMostrar.pra_texto}
                    disabled
                    className="bg-gray-50 min-h-[100px]"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock size={16} className="inline mr-1" />
                  ¿Usa tiempo? <span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="con_tiempo"
                        value="1"
                        checked={conTiempo === 1}
                        onChange={() => setConTiempo(1)}
                        disabled={isSubmitting}
                        className="text-military-500 focus:ring-military-500"
                      />
                      <span className="text-sm font-medium text-gray-700">SI</span>
                    </label>
                    <label className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="con_tiempo"
                        value="0"
                        checked={conTiempo === 0}
                        onChange={() => setConTiempo(0)}
                        disabled={isSubmitting}
                        className="text-military-500 focus:ring-military-500"
                      />
                      <span className="text-sm font-medium text-gray-700">NO</span>
                    </label>
                  </div>
                ) : (
                  <Input
                    value={preguntaMostrar.pra_con_tiempo_nombre || (preguntaMostrar.pra_con_tiempo === 1 ? "SI" : "NO")}
                    disabled
                    className="bg-gray-50"
                  />
                )}
              </div>

              {conTiempo === 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock size={16} className="inline mr-1" />
                    Minutos <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <Input
                      type="number"
                      min="1"
                      value={minutos}
                      onChange={(e) => setMinutos(parseInt(e.target.value) || 0)}
                      disabled={isSubmitting}
                      required
                    />
                  ) : (
                    <Input
                      value={preguntaMostrar.pra_minutos || 0}
                      disabled
                      className="bg-gray-50"
                    />
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar size={16} className="inline mr-1" />
                    Fecha de Apertura
                  </label>
                  <Input
                    value={formatDate(preguntaMostrar.pra_fecha_apertura)}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar size={16} className="inline mr-1" />
                    Fecha de Inicio
                  </label>
                  <Input
                    value={formatDate(preguntaMostrar.pra_fecha_inicio) || "Sin fecha"}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar size={16} className="inline mr-1" />
                    Fecha de Cierre
                  </label>
                  <Input
                    value={formatDate(preguntaMostrar.pra_fecha_cierre) || "Sin fecha"}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
                {isEditing ? (
                  <>
                    <Button
                      onClick={() => {
                        setIsEditing(false);
                        setTexto(preguntaMostrar.pra_texto);
                        setConTiempo(preguntaMostrar.pra_con_tiempo);
                        setMinutos(preguntaMostrar.pra_minutos || 0);
                      }}
                      variant="outline"
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSaveChanges}
                      disabled={isSubmitting}
                      className="flex-1 bg-military-500 hover:bg-military-600 text-white"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save size={20} className="mr-2" />
                          Guardar Cambios
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => setIsEditing(true)}
                      variant="outline"
                      className="flex-1 border-military-500 text-military-600 hover:bg-military-50"
                    >
                      <Edit size={20} className="mr-2" />
                      Modificar
                    </Button>
                    <Button
                      onClick={() => onClose(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cerrar
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
